// src/utils/auth/microsoftProvider.js
// Builds a configured Microsoft OAuth provider for Firebase Auth.
// Business logic:
// - If VITE_MS_TENANT_ID is provided, scope the provider to that tenant (single-tenant).
// - Use prompt=select_account for clearer UX when multiple accounts are present.
// - Scopes are intentionally minimal for authentication-only.
import { OAuthProvider } from 'firebase/auth';

// function _safeSuffix(value, visible = 6) {
//   const v = String(value || '');
//   if (!v) return '';
//   if (v.length <= visible) return v;
//   return `…${v.slice(-visible)}`;
// }

export function buildMicrosoftProvider() {
  // // Console logging policy:
  // // - This file is used in production during the SLaM pilot and during dev/staging testing.
  // // - We log only *non-sensitive* configuration and decision points.
  // // - We do NOT log access tokens, ID tokens, API keys, secrets, or full custom claims.
  // //
  // // These logs are intentionally verbose temporarily to support a short live debugging window.
  // console.log('[TD-SSO] buildMicrosoftProvider: start', {
  //   env: import.meta.env.VITE_ENVIRONMENT || process.env.NODE_ENV,
  //   projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  // });

  const provider = new OAuthProvider('microsoft.com');

  const tenantId =
    import.meta.env.VITE_MS_TENANT_ID &&
    import.meta.env.VITE_MS_TENANT_ID.trim().length > 0
      ? import.meta.env.VITE_MS_TENANT_ID.trim()
      : undefined;

  // Tenant routing rules (Microsoft identity platform):
  // - If VITE_MS_TENANT_ID is set, we lock to that tenant (SLaM prod single-tenant).
  // - Otherwise, we default to 'common' which supports both work/school AND personal accounts.
  // - For local testing with personal accounts only, you can force 'consumers' via
  //   VITE_MS_TEST_TENANT=consumers. This is useful when /common behaves unexpectedly
  //   due to cached sessions or tenant policies during development.
  // CRA `.env` files sometimes include quotes (e.g. VITE_MS_TEST_TENANT="consumers").
  // Strip wrapping quotes so comparisons like `=== 'consumers'` behave as expected.
  const testTenantRaw = (import.meta.env.VITE_MS_TEST_TENANT || '').trim();
  const testTenant = testTenantRaw.replace(/^['"]|['"]$/g, '');
  const effectiveTenant = tenantId || testTenant || 'common';

  const customParams = { prompt: 'select_account', tenant: effectiveTenant };
  provider.setCustomParameters(customParams);

  // console.log('[TD-SSO] buildMicrosoftProvider: configured', {
  //   tenantLocked: Boolean(tenantId),
  //   tenantSuffix: _safeSuffix(tenantId),
  //   testTenant: testTenant || '',
  //   effectiveTenant,
  //   customParams,
  // });

  // Add scopes only if you later need Graph access (mail, calendars, etc.)
  // provider.addScope('User.Read');

  return provider;
}

export function isProjectAllowedToUseMicrosoftSSO() {
  // Enable SSO if either:
  // - A tenant is configured (single-tenant scenarios like SLaM prod), or
  // - An explicit feature flag is turned on for non‑prod testing.
  // This avoids hard‑coding project IDs into the client bundle.
  const hasTenant = !!(import.meta.env.VITE_MS_TENANT_ID && import.meta.env.VITE_MS_TENANT_ID.trim());
  const testToggle = String(import.meta.env.VITE_MS_TEST_ENABLE === 'true');
  const allowed = hasTenant || testToggle === 'true';
  // console.log('[TD-SSO] isProjectAllowedToUseMicrosoftSSO:', {
  //   allowed,
  //   hasTenant,
  //   tenantSuffix: _safeSuffix(import.meta.env.VITE_MS_TENANT_ID),
  //   testEnable: import.meta.env.VITE_MS_TEST_ENABLE,
  //   projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  //   env: import.meta.env.VITE_ENVIRONMENT || process.env.NODE_ENV,
  // });
  return allowed;
}


